import { isString } from 'lodash';
import { useCallback, useEffect } from 'react';
import { GIFSearchComponent } from '@shared/scenes/gifs/components/search';
import * as Actions from '@shared/scenes/gifs/store/actions';
import { useDispatch, useSelector } from '@shared/store';

/*
    Props that the parent of the component is expected to provide.
*/
interface Props {
  onSelect: (gif: MBGIF) => void;
}

export function MBGIFSearch({ onSelect }: Props) {
  const dispatch = useDispatch();

  // Redux state
  const {
    searchQuery,
    trending,
    isError,
    searchResults: allSearchResults,
  } = useSelector(state => state.gifs);
  const isSearching = !!searchQuery;
  const searchResults = (isString(searchQuery) && allSearchResults[searchQuery]) || [];

  // Action creators
  const searchAction = useCallback(
    async (query: string | null, loadMore?: boolean) => {
      return dispatch(Actions.search(query, loadMore));
    },
    [dispatch],
  );

  const handleSearch = useCallback(
    async (query: string) => {
      await searchAction(query);
    },
    [searchAction],
  );

  const fetchTrending = useCallback(() => {
    return dispatch(Actions.fetchTrending());
  }, [dispatch]);

  const loadMoreTrending = useCallback(() => {
    return dispatch(Actions.loadMoreTrending());
  }, [dispatch]);

  // Lifecycle effects
  useEffect(() => {
    // When the component mounts, we want to fetch the trending
    // GIFs so that there's always something to show
    fetchTrending();

    // When the user leaves GIF search, reset the query so that it
    // doesn't affect what's being shown when GIF search is reopened
    return () => {
      searchAction(null);
    };
  }, [fetchTrending, searchAction]);

  const handleLoadMore = useCallback(() => {
    if (isSearching) {
      searchAction(searchQuery, true);
    } else {
      loadMoreTrending();
    }
  }, [isSearching, searchAction, searchQuery, loadMoreTrending]);

  return (
    <GIFSearchComponent
      isLoading={!trending.length}
      isError={!!isError}
      gifs={isSearching ? searchResults : trending}
      onSelect={onSelect}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
    />
  );
}
